/**
 * /directory/:kind — master list of every place of a given kind.
 *
 * Supported kinds:
 *   • us-states, us-counties, us-cities
 *   • indian-states, indian-districts, indian-cities
 *
 * Each row is a small card: <stamp> · <name> · <state/country hint>
 * → clicks through to the location's `/l/<slug>` article.
 *
 * The list itself is pulled from `/api/locations/browse` (existing
 * endpoint used by ExploreUsaLanding etc.) with a country + kind
 * filter, so we don't duplicate the backend catalog.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Search, ArrowLeft } from "lucide-react";
import PlaceStamp from "@/components/PlaceStamp";
import PlaceKindTag from "@/components/PlaceKindTag";

const API = process.env.REACT_APP_BACKEND_URL;

// kind slug → { title, country filter, kind filter, sibling nav }
const KIND_CONFIG = {
  "us-states": {
    title:   "US States",
    country: "united-states",
    kind:    "state",
    tagKind: "state",
    intro:   "All 50 US states — plus DC and territories — pulled from our geographic engine.",
    siblings: [["us-counties", "US Counties"], ["us-cities", "US Cities"]],
  },
  "us-counties": {
    title:   "US Counties",
    country: "united-states",
    kind:    "county",
    tagKind: "county",
    intro:   "Every US county / parish / borough indexed by our nightly OSM sweep.",
    siblings: [["us-states", "US States"], ["us-cities", "US Cities"]],
  },
  "us-cities": {
    title:   "US Cities",
    country: "united-states",
    kind:    "city",
    tagKind: "city",
    intro:   "Cities, towns and villages across the United States.",
    siblings: [["us-states", "US States"], ["us-counties", "US Counties"]],
  },
  "indian-states": {
    title:   "Indian States & Union Territories",
    country: "india",
    kind:    "state",
    tagKind: "state",
    intro:   "All 28 states + 8 Union Territories of India.",
    siblings: [["indian-districts", "Indian Districts"], ["indian-cities", "Indian Cities"]],
  },
  "indian-districts": {
    title:   "Indian Districts",
    country: "india",
    kind:    "district",
    tagKind: "district",
    intro:   "Districts across India — over 780 and growing.",
    siblings: [["indian-states", "Indian States"], ["indian-cities", "Indian Cities"]],
  },
  "indian-cities": {
    title:   "Indian Cities",
    country: "india",
    kind:    "city",
    tagKind: "city",
    intro:   "Cities and towns across India.",
    siblings: [["indian-states", "Indian States"], ["indian-districts", "Indian Districts"]],
  },
};

export default function KindDirectoryPage() {
  const { kind } = useParams();
  const cfg = KIND_CONFIG[kind];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    let alive = true;
    if (!cfg) { setLoading(false); return; }
    setLoading(true);
    // Dedicated kind-directory endpoint — filters by country + kind
    // on the server so we don't page through 50k locations.
    axios.get(`${API}/api/kinds/${kind}`, { params: { limit: 5000 } })
      .then((r) => alive && setItems(r.data?.items || []))
      .catch(() => alive && setItems([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [kind, cfg]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((i) => (i.name || "").toLowerCase().includes(needle) ||
                               (i.state || "").toLowerCase().includes(needle));
  }, [items, q]);

  if (!cfg) {
    return (
      <div className="max-w-3xl mx-auto py-16 text-center">
        <h1 className="font-display text-3xl mb-2">Directory not found</h1>
        <p className="opacity-70">Unknown directory kind: {kind}</p>
        <div className="mt-6 flex gap-2 justify-center flex-wrap">
          {Object.keys(KIND_CONFIG).map((k) => (
            <Link key={k} to={`/directory/${k}`} className="text-sm underline">{KIND_CONFIG[k].title}</Link>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-6 py-10" data-testid={`kind-directory-${kind}`}>
      <Link to="/" className="text-xs font-mono uppercase tracking-widest opacity-70 hover:underline inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-3 h-3" /> Home
      </Link>
      <h1 className="font-display text-4xl sm:text-5xl leading-tight mb-1">{cfg.title}</h1>
      <p className="text-sm opacity-70 mb-6 max-w-[52ch]">{cfg.intro}</p>

      {/* Sibling directory chips — jump between State/County/City lists */}
      <div className="mb-6 flex flex-wrap gap-2" data-testid="kind-siblings">
        {cfg.siblings.map(([s, label]) => (
          <Link
            key={s}
            to={`/directory/${s}`}
            className="text-xs font-mono uppercase tracking-widest rounded-full px-3 py-1.5 border-2 hover:brightness-95 transition"
            style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
            data-testid={`kind-sibling-${s}`}
          >
            {label} →
          </Link>
        ))}
      </div>

      {/* Search */}
      <div className="mb-6 relative">
        <Search className="w-4 h-4 absolute left-3 top-3 opacity-50" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={`Search ${cfg.title.toLowerCase()}…`}
          className="w-full rounded-lg border pl-9 pr-3 py-2 text-sm bg-transparent"
          style={{ borderColor: "var(--wofy-border)" }}
          data-testid="kind-directory-search"
        />
      </div>

      {loading ? (
        <div className="text-sm opacity-60"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="wofy-card text-center py-12">
          <p className="text-sm opacity-70">No entries indexed yet — our Geographic Discovery Engine will populate this shortly.</p>
        </div>
      ) : (
        <>
          <div className="mb-3 text-xs font-mono opacity-60">{filtered.length} of {items.length}</div>
          <ul
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2"
            data-testid="kind-directory-list"
          >
            {filtered.map((it) => (
              <li key={it.slug}>
                <Link
                  to={`/l/${it.slug}`}
                  className="group flex items-start gap-3 rounded-xl border p-3 hover:shadow-md hover:-translate-y-0.5 transition"
                  style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
                  data-testid={`kind-directory-item-${it.slug}`}
                >
                  <PlaceStamp
                    kind={cfg.tagKind}
                    slug={it.slug}
                    override={it.stamp_url}
                    size={44}
                    className="shadow-sm ring-1 ring-white/60"
                    title={it.name}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium group-hover:underline truncate">{it.name}</div>
                    {(it.state || it.country) && (
                      <div className="text-xs opacity-70 mt-0.5 truncate">
                        {[it.state, it.country].filter(Boolean).join(" · ")}
                      </div>
                    )}
                    <div className="mt-1">
                      <PlaceKindTag kind={cfg.tagKind} slug={it.slug} country={cfg.country} />
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/** Slug of the directory that lists all peers of the given place. */
export function directorySlugFor(kind, country) {
  const c = String(country || "").toLowerCase();
  const isUS = c === "united states" || c === "united-states" || c === "usa" || c === "us";
  const isIN = c === "india";
  const k = (kind || "").toLowerCase();
  if (isUS) {
    if (k === "state") return "us-states";
    if (k === "county") return "us-counties";
    if (k === "city" || k === "town" || k === "village") return "us-cities";
  }
  if (isIN) {
    if (k === "state") return "indian-states";
    if (k === "district" || k === "county") return "indian-districts";
    if (k === "city" || k === "town" || k === "village") return "indian-cities";
  }
  return null;
}
